import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Slide,
  Box,
  Divider
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import SwapHorizIcon from '@material-ui/icons/SwapHoriz'

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      maxWidth: 520,
      background: 'linear-gradient(145deg, #1a1f36 0%, #0d1225 100%)',
      border: '1px solid rgba(99, 179, 237, 0.2)',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(99, 179, 237, 0.1)',
    }
  },
  dialogTitle: {
    padding: theme.spacing(3),
    background: 'linear-gradient(90deg, rgba(66, 153, 225, 0.15) 0%, rgba(99, 179, 237, 0.05) 100%)',
    borderBottom: '1px solid rgba(99, 179, 237, 0.15)',
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  iconWrapper: {
    width: 56,
    height: 56,
    borderRadius: 14,
    background: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(66, 153, 225, 0.3)',
  },
  icon: {
    fontSize: 28,
    color: '#fff',
  },
  titleText: {
    fontWeight: 700,
    fontSize: '1.35rem',
    color: '#e2e8f0',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '0.85rem',
    color: '#718096',
    marginTop: 2,
  },
  dialogContent: {
    padding: theme.spacing(4),
  },
  messageBox: {
    background: 'linear-gradient(145deg, rgba(66, 153, 225, 0.08) 0%, rgba(49, 130, 206, 0.04) 100%)',
    borderRadius: 12,
    padding: theme.spacing(3),
    border: '1px solid rgba(99, 179, 237, 0.12)',
    marginBottom: theme.spacing(3),
  },
  messageTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  swapIcon: {
    color: '#4299e1',
    fontSize: 24,
  },
  messageTitleText: {
    color: '#90cdf4',
    fontWeight: 600,
    fontSize: '1rem',
  },
  messageText: {
    color: '#cbd5e0',
    fontSize: '0.95rem',
    lineHeight: 1.7,
    marginBottom: theme.spacing(2),
  },
  highlight: {
    color: '#90cdf4',
    fontWeight: 500,
  },
  divider: {
    backgroundColor: 'rgba(99, 179, 237, 0.15)',
    margin: theme.spacing(2, 0),
  },
  infoBox: {
    background: 'rgba(237, 137, 54, 0.08)',
    borderRadius: 10,
    padding: theme.spacing(2),
    border: '1px solid rgba(237, 137, 54, 0.2)',
    display: 'flex',
    gap: theme.spacing(1.5),
  },
  infoIcon: {
    color: '#ed8936',
    fontSize: 20,
    marginTop: 2,
  },
  infoText: {
    color: '#fbd38d',
    fontSize: '0.85rem',
    lineHeight: 1.6,
  },
  dialogActions: {
    padding: theme.spacing(2, 3, 3, 3),
    borderTop: '1px solid rgba(99, 179, 237, 0.1)',
  },
  button: {
    borderRadius: 10,
    padding: theme.spacing(1.25, 4),
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '0.95rem',
    background: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)',
    color: '#fff',
    boxShadow: '0 4px 16px rgba(66, 153, 225, 0.3)',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #5faee8 0%, #4299e1 100%)',
      boxShadow: '0 6px 20px rgba(66, 153, 225, 0.4)',
      transform: 'translateY(-1px)',
    },
  },
  footer: {
    textAlign: 'center',
    marginTop: theme.spacing(1),
  },
  footerText: {
    color: '#4a5568',
    fontSize: '0.75rem',
  },
  badge: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginLeft: theme.spacing(1),
  }
}))

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

const MIGRATION_STORAGE_KEY = 'chlsMigration_pending'

const ChslMigrationModal = () => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const [canClean, setCanClean] = useState(false)

  // Vérifier au montage si une migration est en attente
  useEffect(() => {
    const hasPendingMigration = localStorage.getItem(MIGRATION_STORAGE_KEY) === 'true'
    if (hasPendingMigration) {
      setOpen(true)
    }
  }, [])

  // Effet pour permettre le nettoyage après 2 secondes d'affichage
  useEffect(() => {
    let timer
    if (open) {
      timer = setTimeout(() => {
        setCanClean(true)
      }, 2000)
    }
    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [open])

  const handleClose = () => {
    setOpen(false)
    // Toujours supprimer du localStorage quand l'utilisateur clique sur "J'ai compris"
    localStorage.removeItem(MIGRATION_STORAGE_KEY)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      className={classes.dialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.titleContent}>
          <Box className={classes.iconWrapper}>
            <SwapHorizIcon className={classes.icon} />
          </Box>
          <Box>
            <Typography className={classes.titleText}>
              Migration effectuée
              <span className={classes.badge}>Nouveau</span>
            </Typography>
            <Typography className={classes.subtitle}>
              Système médical LSMC / SSMC / LVMC (+ Fire Department)
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.messageBox}>
          <Box className={classes.messageTitle}>
            <SwapHorizIcon className={classes.swapIcon} />
            <Typography className={classes.messageTitleText}>
              Redirection automatique
            </Typography>
          </Box>
          
          <Typography className={classes.messageText}>
            Vous avez été <span className={classes.highlight}>redirigé automatiquement</span> depuis 
            l'ancien site médical <strong>(CHLS)</strong> vers le <span className={classes.highlight}>nouveau système médical intégré</span>.
          </Typography>
          
          <Typography className={classes.messageText} style={{ marginBottom: 0 }}>
            Cette migration fait partie de l'unification des services médicaux sur la nouvelle plateforme TASEv3 / CAD, 
            offrant une meilleure expérience utilisateur et des fonctionnalités améliorées.
          </Typography>
        </Box>

        <Divider className={classes.divider} />

        <Box className={classes.infoBox}>
          <InfoOutlinedIcon className={classes.infoIcon} />
          <Typography className={classes.infoText}>
            Si vous n'êtes pas au courant de ce changement, merci de <strong>contacter la direction de votre service</strong> ou 
            de vous rapprocher de vos collègues pour plus d'informations.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Box style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Button
            onClick={handleClose}
            className={classes.button}
            variant="contained"
          >
            J'ai compris
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default ChslMigrationModal